import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Loader2, Gift } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

export default function Login() {
  const [, setLocation] = useLocation();
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  // AppSumo integration
  const [appsumoLicense, setAppsumoLicense] = useState<string | null>(null);
  const [appsumoTier, setAppsumoTier] = useState<number | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const license = params.get("appsumo");
    const tier = params.get("tier");
    const message = params.get("message");
    
    if (license) setAppsumoLicense(license);
    if (tier) setAppsumoTier(parseInt(tier) || 1);
    
    // Handle "license already active" message
    if (message === "license_already_active") {
      setError("This license is already linked to an account. Please sign in.");
    }
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    const { error } = await signIn(email, password);

    if (error) {
      setError(error);
      setIsLoading(false);
      return;
    }

    // If this is an AppSumo login, try to link the license
    if (appsumoLicense) {
      try {
        const res = await api.post("/api/appsumo/link", { licenseUuid: appsumoLicense });
        if (res.ok) {
          setLocation("/dashboard?appsumo=activated");
          return;
        }
        // If linking fails (e.g., already linked), just redirect to dashboard
      } catch {
        // Continue to dashboard even if linking fails
      }
    }

    setLocation("/dashboard");
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Link href="/" className="flex flex-col items-center gap-2">
            <img src="/logo.png" alt="ReviewKit" className="h-12 w-auto object-contain" />
            <span className="font-bold text-xl text-muted-foreground">ReviewKit</span>
          </Link>
          <CardTitle className="mt-4">Welcome back</CardTitle>
          <CardDescription>
            {appsumoLicense 
              ? "Sign in to link your AppSumo license"
              : "Sign in to your account to continue"}
          </CardDescription>
          
          {/* Show AppSumo badge if coming from AppSumo */}
          {appsumoLicense && (
            <div className="flex justify-center mt-2">
              <Badge variant="secondary" className="flex items-center gap-1">
                <Gift className="w-3 h-3" />
                AppSumo Tier {appsumoTier || 1}
              </Badge>
            </div>
          )}
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link href="/forgot-password" className="text-sm text-primary hover:underline">
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {appsumoLicense ? "Signing in & Activating..." : "Signing in..."}
                </>
              ) : (
                appsumoLicense ? "Sign in & Activate" : "Sign in"
              )}
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              Don't have an account?{" "}
              <Link 
                href={`/signup${appsumoLicense ? `?appsumo=${appsumoLicense}&tier=${appsumoTier}` : ""}`} 
                className="text-primary hover:underline"
              >
                Sign up
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
